import React, { useState } from 'react';
import { CalendarDays, List } from 'lucide-react';
import AcademicCalendar from '../../academic-management/components/AcademicCalendar';
import EventModal from '../../academic-management/components/EventModal';
import { academicCalendarService } from '../../../services/academicCalendarService';

/**
 * Institution-wide calendar workspace.
 *
 * Academic years, semesters and calendar events are global controls, so they
 * belong with System Settings rather than inside a department's programmes
 * and units workspace.
 */
const SystemCalendarSettings = ({ headerActions }) => {
    const [view, setView] = useState('archive');
    const [refreshKey, setRefreshKey] = useState(0);
    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const refresh = () => setRefreshKey(current => current + 1);

    const handleSaveEvent = async eventData => {
        if (selectedEvent?.id) {
            await academicCalendarService.updateCalendarEvent(selectedEvent.id, eventData);
        } else {
            await academicCalendarService.createCalendarEvent(eventData);
        }
        setShowEventModal(false);
        setSelectedEvent(null);
        refresh();
    };

    const openNewEvent = () => {
        setSelectedEvent(null);
        setShowEventModal(true);
    };

    const openEvent = event => {
        setSelectedEvent(event);
        setShowEventModal(true);
    };

    return (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-5 py-4 lg:flex-nowrap">
                <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-1 lg:flex-nowrap">
                    <h2 className="text-base font-bold tracking-tight text-slate-950">Institution calendar</h2>
                    <p className="min-w-0 truncate text-xs text-slate-500">Manage academic years, semesters and institution-wide events in one place.</p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 lg:flex-nowrap">
                    {headerActions}
                    <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm" role="tablist" aria-label="Calendar view">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'archive'}
                        onClick={() => setView('archive')}
                        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === 'archive' ? 'bg-primary text-primary-foreground' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                        <CalendarDays className="h-3.5 w-3.5" /> Academic periods
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={view === 'events'}
                        onClick={() => setView('events')}
                        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === 'events' ? 'bg-primary text-primary-foreground' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                        <List className="h-3.5 w-3.5" /> Events
                    </button>
                    </div>
                </div>
            </div>

            <div className="p-5 md:p-6">
                <AcademicCalendar
                    key={view}
                    view={view}
                    refreshKey={refreshKey}
                    onAddEvent={openNewEvent}
                    onEditEvent={openEvent}
                    onRefresh={refresh}
                />
            </div>

            <EventModal
                isOpen={showEventModal}
                onClose={() => {
                    setShowEventModal(false);
                    setSelectedEvent(null);
                }}
                onSave={handleSaveEvent}
                event={selectedEvent}
            />
        </section>
    );
};

export default SystemCalendarSettings;
